/**
 * Maps prototype + version → markup-editor drawing id.
 *
 * localStorage alone is fragile: clearing site data drops the pointer while the
 * IndexedDB drawing (with htmlScreenRoutes) still exists. This store survives
 * localStorage clears so comment redirect keeps working.
 */

const IDB_NAME = 'pmx-prototype-drawing-cache'
const STORE = 'links'

const linkKey = (prototypeId, versionId) =>
    `${String(prototypeId || '')}:${String(versionId || 'current')}`

const openDb = () =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, 1)
        req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'key' })
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })

export const readDrawingLink = async (prototypeId, versionId) => {
    const key = linkKey(prototypeId, versionId)
    if (!key.startsWith(':') && key !== ':current') {
        try {
            const db = await openDb()
            try {
                const row = await new Promise((resolve, reject) => {
                    const tx = db.transaction(STORE, 'readonly')
                    const req = tx.objectStore(STORE).get(key)
                    req.onsuccess = () => resolve(req.result || null)
                    req.onerror = () => reject(req.error)
                })
                const id = String(row?.drawingId || '').trim()
                if (id) return id
            } finally {
                db.close()
            }
        } catch {
            // fall through to localStorage
        }
    }
    return null
}

export const writeDrawingLink = async (
    prototypeId,
    versionId,
    drawingId
) => {
    const key = linkKey(prototypeId, versionId)
    const id = String(drawingId || '').trim()
    if (!id) return
    try {
        const db = await openDb()
        try {
            await new Promise((resolve, reject) => {
                const tx = db.transaction(STORE, 'readwrite')
                tx.objectStore(STORE).put({
                    key,
                    drawingId: id,
                    prototypeId: String(prototypeId || ''),
                    versionId: String(versionId || 'current'),
                    updatedAt: new Date().toISOString(),
                })
                tx.oncomplete = () => resolve()
                tx.onerror = () => reject(tx.error)
            })
        } finally {
            db.close()
        }
    } catch {
        // best-effort; localStorage still holds a copy
    }
}

export const clearDrawingLink = async (prototypeId, versionId) => {
    const key = linkKey(prototypeId, versionId)
    try {
        const db = await openDb()
        try {
            await new Promise((resolve, reject) => {
                const tx = db.transaction(STORE, 'readwrite')
                tx.objectStore(STORE).delete(key)
                tx.oncomplete = () => resolve()
                tx.onerror = () => reject(tx.error)
            })
        } finally {
            db.close()
        }
    } catch {
        // ignore
    }
}

/** IndexedDB link first, then legacy localStorage key. */
export const resolveCachedDrawingId = async (prototypeId, versionId) => {
    const fromIdb = await readDrawingLink(prototypeId, versionId)
    if (fromIdb) return fromIdb
    try {
        const legacyKey = `pmx-prototype-drawing-v16:${prototypeId}:${
            versionId || 'current'
        }`
        return (
            localStorage.getItem(legacyKey) ||
            sessionStorage.getItem(legacyKey) ||
            null
        )
    } catch {
        return null
    }
}

export const persistCachedDrawingId = async (
    prototypeId,
    versionId,
    drawingId
) => {
    const id = String(drawingId || '').trim()
    if (!id) return
    await writeDrawingLink(prototypeId, versionId, id)
    try {
        const legacyKey = `pmx-prototype-drawing-v16:${prototypeId}:${
            versionId || 'current'
        }`
        localStorage.setItem(legacyKey, id)
    } catch {
        // ignore
    }
}

export const clearCachedDrawingIdEverywhere = async (
    prototypeId,
    versionId
) => {
    await clearDrawingLink(prototypeId, versionId)
    try {
        const legacyKey = `pmx-prototype-drawing-v16:${prototypeId}:${
            versionId || 'current'
        }`
        localStorage.removeItem(legacyKey)
        sessionStorage.removeItem(legacyKey)
    } catch {
        // ignore
    }
}
